import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface SplitPanelContextValue {
  header: string;
  content: ReactNode | null;
  isOpen: boolean;
  show: (header: string, content: ReactNode) => void;
  clear: () => void;
  setOpen: (open: boolean) => void;
}

const SplitPanelContext = createContext<SplitPanelContextValue | null>(null);

interface SplitPanelProviderProps {
  children: ReactNode;
}

export function SplitPanelProvider({ children }: SplitPanelProviderProps): ReactNode {
  const [header, setHeader] = useState('');
  const [content, setContent] = useState<ReactNode | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const closedByUser = useRef(false);

  const show = useCallback((nextHeader: string, nextContent: ReactNode) => {
    setHeader(nextHeader);
    setContent(nextContent);
    if (!closedByUser.current) {
      setIsOpen(true);
    }
  }, []);

  const clear = useCallback(() => {
    setHeader('');
    setContent(null);
    setIsOpen(false);
    closedByUser.current = false;
  }, []);

  const setOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    closedByUser.current = !open;
  }, []);

  const value = useMemo(
    () => ({ header, content, isOpen, show, clear, setOpen }),
    [header, content, isOpen, show, clear, setOpen],
  );

  return <SplitPanelContext.Provider value={value}>{children}</SplitPanelContext.Provider>;
}

export function useSplitPanel(): SplitPanelContextValue {
  const ctx = useContext(SplitPanelContext);
  if (!ctx) {
    throw new Error('useSplitPanel must be used within SplitPanelProvider');
  }
  return ctx;
}
